import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  FileText, 
  MessageSquare, 
  Upload, 
  Calendar, 
  DollarSign, 
  AlertTriangle, 
  TrendingUp,
  Award,
  Eye,
  Download,
  Send,
  Plus,
  Edit,
  Star,
  MapPin,
  Users,
  Briefcase
} from 'lucide-react';

const VerificationProposalWorkspace = ({ verifierId = 'V001' }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');

  // Mock data for verifier's proposals and projects
  const [proposals] = useState([
    {
      id: 'PR001',
      requestId: 'VR001',
      projectName: 'Amazon Rainforest Restoration',
      projectOwner: 'Green Earth Foundation',
      status: 'pending',
      submittedDate: '2024-01-18',
      proposedPrice: 75000,
      timeline: '4 months',
      methodology: 'Gold Standard CDM methodology with satellite monitoring',
      scope: 'Full project verification including baseline, monitoring, and community impact assessment',
      projectType: 'Reforestation',
      location: 'Brazil',
      estimatedCredits: 150000,
      budgetRange: '50000-100000',
      deadline: '2024-04-15'
    },
    {
      id: 'PR005',
      requestId: 'VR005',
      projectName: 'Coastal Mangrove Protection',
      projectOwner: 'Ocean Conservation Trust',
      status: 'accepted',
      submittedDate: '2024-01-12',
      acceptedDate: '2024-01-20',
      proposedPrice: 52000,
      timeline: '3 months',
      methodology: 'Blue Carbon methodology with drone monitoring',
      scope: 'Comprehensive verification of blue carbon sequestration',
      projectType: 'Blue Carbon',
      location: 'Philippines',
      estimatedCredits: 95000,
      budgetRange: '40000-60000',
      deadline: '2024-03-15',
      progress: 65,
      milestones: [
        { id: 1, name: 'Initial Assessment', status: 'completed', date: '2024-01-25' },
        { id: 2, name: 'Site Inspection', status: 'completed', date: '2024-02-08' },
        { id: 3, name: 'Data Collection', status: 'in-progress', date: '2024-02-22' },
        { id: 4, name: 'Analysis & Report', status: 'pending', date: '2024-03-08' },
        { id: 5, name: 'Final Verification', status: 'pending', date: '2024-03-15' }
      ]
    }
  ]);

  const [activeProjects] = useState([
    {
      id: 'VP001',
      projectName: 'Wind Energy Development',
      projectOwner: 'Renewable Solutions Inc',
      startDate: '2024-01-05',
      expectedCompletion: '2024-03-05',
      progress: 45,
      contractValue: 125000,
      projectType: 'Renewable Energy',
      location: 'Texas, USA',
      estimatedCredits: 180000,
      status: 'in-progress',
      nextMilestone: 'Field Verification',
      nextMilestoneDate: '2024-02-15',
      documents: [
        { name: 'Project Design Document', type: 'PDF', uploadDate: '2024-01-05', status: 'approved' },
        { name: 'Baseline Study', type: 'PDF', uploadDate: '2024-01-12', status: 'under-review' },
        { name: 'Monitoring Plan', type: 'PDF', uploadDate: '2024-01-18', status: 'pending' }
      ],
      communications: [
        { date: '2024-01-20', type: 'message', from: 'Project Owner', content: 'Additional baseline data uploaded' },
        { date: '2024-01-18', type: 'report', from: 'Verifier', content: 'Initial assessment report submitted' },
        { date: '2024-01-15', type: 'meeting', from: 'System', content: 'Kickoff meeting scheduled for Jan 22' }
      ]
    }
  ]);

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'accepted': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'under-review': 'bg-orange-100 text-orange-800',
      'approved': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleSendMessage = async () => {
    if (!messageContent.trim() || !selectedProject) return;
    
    try {
      console.log('Sending message:', {
        projectId: selectedProject.id,
        content: messageContent,
        verifierId
      });
      
      alert('Message sent successfully!');
      setMessageContent('');
      setShowMessageModal(false);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Proposals</p>
              <p className="text-2xl font-semibold text-gray-900">
                {proposals.filter(p => p.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Projects</p>
              <p className="text-2xl font-semibold text-gray-900">{activeProjects.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Contract Value</p>
              <p className="text-2xl font-semibold text-gray-900">$385k</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Star className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Success Rate</p>
              <p className="text-2xl font-semibold text-gray-900">94%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Proposals</h3>
          <div className="space-y-4">
            {proposals.slice(0, 3).map(proposal => (
              <div key={proposal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{proposal.projectName}</h4>
                  <p className="text-sm text-gray-600">{proposal.projectOwner}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                    {proposal.status}
                  </span>
                  <span className="text-sm font-medium text-gray-900">${proposal.proposedPrice.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Projects</h3>
          <div className="space-y-4">
            {activeProjects.map(project => (
              <div key={project.id} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{project.projectName}</h4>
                  <span className="text-sm font-medium text-gray-900">{project.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Next: {project.nextMilestone}</span>
                  <span>{new Date(project.nextMilestoneDate).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderProposals = () => (
    <div className="space-y-6">
      {proposals.map(proposal => (
        <div key={proposal.id} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{proposal.projectName}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(proposal.status)}`}>
                  {proposal.status}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-4 w-4" />
                  <span>{proposal.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{proposal.projectOwner}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>Deadline: {new Date(proposal.deadline).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-green-600">${proposal.proposedPrice.toLocaleString()}</div>
              <div className="text-sm text-gray-500">{proposal.timeline}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-1">Methodology</h4>
              <p className="text-sm text-gray-700">{proposal.methodology}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-1">Scope</h4>
              <p className="text-sm text-gray-700">{proposal.scope}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Submitted: {new Date(proposal.submittedDate).toLocaleDateString()}
              {proposal.acceptedDate && (
                <span className="ml-4">Accepted: {new Date(proposal.acceptedDate).toLocaleDateString()}</span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {proposal.status === 'pending' && (
                <button className="px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg hover:bg-blue-50">
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit Proposal
                </button>
              )}
              {proposal.status === 'accepted' && (
                <button
                  onClick={() => setSelectedProject(proposal)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Eye className="h-4 w-4 inline mr-1" />
                  View Project
                </button>
              )}
              <button
                onClick={() => {
                  setSelectedProject(proposal);
                  setShowMessageModal(true);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <MessageSquare className="h-4 w-4 inline mr-1" />
                Message
              </button>
            </div>
          </div>

          {proposal.status === 'accepted' && proposal.progress !== undefined && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Project Progress</span>
                <span className="text-sm font-medium text-gray-900">{proposal.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${proposal.progress}%` }}
                ></div>
              </div>
              {proposal.milestones && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-2">
                    {proposal.milestones.map(milestone => (
                      <span
                        key={milestone.id}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`}
                      >
                        {milestone.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {proposals.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No proposals yet</h3>
          <p className="text-gray-600">Start browsing verification requests to submit your first proposal</p>
        </div>
      )}
    </div>
  );

  const renderActiveProjects = () => (
    <div className="space-y-6">
      {activeProjects.map(project => (
        <div key={project.id} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{project.projectName}</h3>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                <div className="flex items-center space-x-1">
                  <MapPin className="h-4 w-4" />
                  <span>{project.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{project.projectOwner}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>Due: {new Date(project.expectedCompletion).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-green-600">${project.contractValue.toLocaleString()}</div>
              <div className="text-sm text-gray-500">{project.estimatedCredits.toLocaleString()} tCO2e</div>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Overall Progress</span>
              <span className="text-sm font-medium text-gray-900">{project.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-green-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${project.progress}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-2 text-sm text-gray-600">
              <span>Next Milestone: {project.nextMilestone}</span>
              <span>{new Date(project.nextMilestoneDate).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Documents */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Project Documents</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {project.documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                      <p className="text-xs text-gray-500">{new Date(doc.uploadDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                      {doc.status}
                    </span>
                    <button className="text-gray-400 hover:text-gray-600">
                      <Download className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Communications */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Recent Communications</h4>
            <div className="space-y-2">
              {project.communications.slice(0, 3).map((comm, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <MessageSquare className="h-4 w-4 text-gray-500 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-900">{comm.from}</span>
                      <span className="text-xs text-gray-500">{new Date(comm.date).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-gray-700">{comm.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                {project.status.replace('-', ' ')}
              </span>
              <span className="text-sm text-gray-500">
                Started: {new Date(project.startDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button className="px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg hover:bg-blue-50">
                <Upload className="h-4 w-4 inline mr-1" />
                Upload Report
              </button>
              <button
                onClick={() => {
                  setSelectedProject(project);
                  setShowMessageModal(true);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <MessageSquare className="h-4 w-4 inline mr-1" />
                Message Owner
              </button>
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                <Eye className="h-4 w-4 inline mr-1" />
                View Details
              </button>
            </div>
          </div>
        </div>
      ))}

      {activeProjects.length === 0 && (
        <div className="text-center py-12">
          <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No active projects</h3>
          <p className="text-gray-600">Submit proposals to start working on verification projects</p>
        </div>
      )}
    </div>
  );

  const renderMessageModal = () => {
    if (!showMessageModal || !selectedProject) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-lg w-full">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Send Message</h2>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600">To: {selectedProject.projectOwner}</p>
              <p className="text-sm text-gray-600">Project: {selectedProject.projectName}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Type your message..."
              />
            </div>

            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!messageContent.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4 inline mr-1" />
                Send Message
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'proposals', label: 'My Proposals', icon: FileText },
    { id: 'active-projects', label: 'Active Projects', icon: Clock },
    { id: 'completed', label: 'Completed', icon: CheckCircle }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'proposals':
        return renderProposals();
      case 'active-projects':
        return renderActiveProjects();
      case 'completed':
        return <div className="text-center py-12 text-gray-500">Completed projects view</div>;
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Verification Workspace</h1>
          <p className="text-gray-600 mt-2">
            Manage your verification proposals and track project progress
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-1 mb-8 border-b border-gray-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        {renderTabContent()}

        {/* Message Modal */}
        {renderMessageModal()}
      </div>
    </div>
  );
};

export default VerificationProposalWorkspace; 